import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/Layout/AppLayout';
import { useMembers, getPlanLimitInfo, type PlanLimitInfo, type BulkInviteResponse } from '../hooks/useMembers';
import { useInviteLink } from '../hooks/useInviteLink';
import { useAuth } from '../hooks/useAuth';
import { UserPlus, UserMinus, ShieldAlert, Mail, Link2, Copy, Check, AlertTriangle, Pencil, X, Search, UserCog } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Modal } from '../components/common/Modal';
import { RowMenu, type ItemDoMenu } from '../components/common/RowMenu';
import { Paginacao, paginar } from '../components/common/Paginacao';
import { useFeedback } from '../components/common/Feedback';
import { filtrarMembros, statusDoMembro, type FiltroDePapel, type FiltroDeStatus } from '../utils/membros';
import { formatarNome, nomeDoPapel } from '../utils/nomes';
import { formatNumber } from '../utils/formatters';

const MAX_BULK_INVITES = 50;
const EXTRA_USER_PRICE = 'R$ 10/mês';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MEMBROS_POR_PAGINA = 20;

const fieldStyle: React.CSSProperties = { background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' };
const labelStyle: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' };
const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-6)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, fontWeight: 'var(--font-medium)' });
const secondaryButtonStyle: React.CSSProperties = { padding: 'var(--space-3) var(--space-5)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)', borderRadius: 'var(--radius-md)', cursor: 'pointer' };

/** Quebra o texto colado em e-mails (vírgula, ponto e vírgula, espaço ou linha), sem repetidos. */
function parseEmails(raw: string) {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const piece of raw.split(/[\n,;\s]+/)) {
    const email = piece.trim();
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    (EMAIL_RE.test(email) ? valid : invalid).push(email);
  }
  return { valid, invalid };
}

function errorMessage(err: unknown, fallback: string) {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function Members() {
  const { members, isLoading, fetchMembers, inviteMembersBulk, updateMemberRole, updateMemberName, removeMember } = useMembers();
  const { membership, user } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const isOwner = membership?.role === 'owner';

  const [activeTab, setActiveTab] = useState<'email' | 'link'>('email');

  // --- Convite por e-mail (em massa) ---
  const [emailsText, setEmailsText] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<BulkInviteResponse | null>(null);
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);
  const [planLimitAck, setPlanLimitAck] = useState(false);

  const parsed = useMemo(() => parseEmails(emailsText), [emailsText]);
  const tooMany = parsed.valid.length > MAX_BULK_INVITES;

  // --- Link de convite ---
  const { link, plan, fetchLink, generateLink, revokeLink } = useInviteLink();
  const [linkDomain, setLinkDomain] = useState(() => user?.email?.split('@')[1] ?? '');
  const [linkHours, setLinkHours] = useState(1);
  const [linkAck, setLinkAck] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // --- Edição do nome do membro (vale só nesta organização) ---
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  // --- A1: busca, filtros e paginação da lista ---
  const { confirmar, avisar } = useFeedback();
  const [busca, setBusca] = useState('');
  const [filtroPapel, setFiltroPapel] = useState<FiltroDePapel>('todos');
  const [filtroStatus, setFiltroStatus] = useState<FiltroDeStatus>('todos');
  const [pagina, setPagina] = useState(1);
  const membrosFiltrados = useMemo(
    () => filtrarMembros(members, { busca, papel: filtroPapel, status: filtroStatus }),
    [members, busca, filtroPapel, filtroStatus]
  );
  const ultimaPagina = Math.max(1, Math.ceil(membrosFiltrados.length / MEMBROS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, ultimaPagina);
  const membrosDaPagina = paginar(membrosFiltrados, paginaAtual, MEMBROS_POR_PAGINA);
  const temFiltro = busca.trim() !== '' || filtroPapel !== 'todos' || filtroStatus !== 'todos';

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    if (isAdmin && activeTab === 'link') {
      fetchLink().catch((err) => setLinkError(errorMessage(err, 'Erro ao carregar o link de convite.')));
    }
  }, [isAdmin, activeTab, fetchLink]);

  const sendInvites = async (confirmExtraCharges: boolean) => {
    setIsInviting(true);
    setInviteError(null);
    try {
      const result = await inviteMembersBulk(parsed.valid, inviteRole, confirmExtraCharges);
      setInviteResult(result);
      setEmailsText('');
      setPlanLimit(null);
    } catch (err) {
      const limit = getPlanLimitInfo(err);
      if (limit) {
        // Passou do limite do plano: abre o aviso e só segue se o admin confirmar
        setPlanLimitAck(false);
        setPlanLimit(limit);
      } else {
        setInviteError(errorMessage(err, 'Erro ao enviar convites.'));
      }
    } finally {
      setIsInviting(false);
    }
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (parsed.valid.length === 0 || tooMany) return;
    setInviteResult(null);
    sendInvites(false);
  };

  const handleGenerateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (link && !(await confirmar({ titulo: 'Gerar um novo link?', mensagem: 'Gerar um novo link desativa o link atual na hora.', confirmar: 'Gerar novo link' }))) return;
    setIsGenerating(true);
    setLinkError(null);
    try {
      await generateLink(linkDomain, linkHours, linkAck);
      setCopied(false);
    } catch (err) {
      setLinkError(errorMessage(err, 'Erro ao gerar o link.'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeLink = async () => {
    if (!(await confirmar({ titulo: 'Desativar o link?', mensagem: 'Quem ainda não entrou não conseguirá mais usá-lo.', confirmar: 'Desativar link', perigo: true }))) return;
    try {
      await revokeLink();
    } catch (err) {
      setLinkError(errorMessage(err, 'Erro ao desativar o link.'));
    }
  };

  const handleCopyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setLinkError('Não foi possível copiar automaticamente. Selecione o link e copie manualmente.');
    }
  };

  const startEditingName = (id: string, currentName: string) => {
    setEditingNameId(id);
    setNameDraft(currentName);
  };

  const saveName = async () => {
    if (!editingNameId) return;
    setIsSavingName(true);
    try {
      await updateMemberName(editingNameId, nameDraft);
      setEditingNameId(null);
    } catch (err) {
      avisar(errorMessage(err, 'Erro ao salvar o nome.'), 'erro');
    } finally {
      setIsSavingName(false);
    }
  };

  const nomeNaLista = (m: { name: string; email: string }) => formatarNome(m.name) || m.email;

  // A1: trocar papel e remover pedem confirmação citando a pessoa.
  const handleRoleChange = async (member: { id: string; name: string; email: string }, newRole: string) => {
    const ok = await confirmar({
      titulo: `Tornar ${nomeNaLista(member)} ${nomeDoPapel(newRole).toLowerCase()}?`,
      mensagem: newRole === 'owner'
        ? 'Proprietários têm acesso total à organização, inclusive ao plano e à cobrança.'
        : newRole === 'admin'
          ? 'Administradores podem cadastrar e editar credenciais e gerenciar membros e grupos.'
          : 'Membros só usam as credenciais liberadas para os grupos deles, sem ver as senhas.',
      confirmar: 'Alterar papel',
    });
    if (!ok) return;
    try {
      await updateMemberRole(member.id, newRole);
      avisar(`Papel de ${nomeNaLista(member)} alterado para ${nomeDoPapel(newRole)}.`, 'sucesso');
    } catch (err) {
      avisar(errorMessage(err, 'Erro ao alterar o papel.'), 'erro');
    }
  };

  const handleRemove = async (member: { id: string; name: string; email: string }) => {
    const ok = await confirmar({
      titulo: `Remover ${nomeNaLista(member)} da organização?`,
      mensagem: `${member.email} perde o acesso a todas as credenciais desta organização na hora.`,
      confirmar: 'Remover',
      perigo: true,
    });
    if (!ok) return;
    try {
      await removeMember(member.id);
      avisar(`${nomeNaLista(member)} foi removido(a) da organização.`, 'sucesso');
    } catch (err) {
      avisar(errorMessage(err, 'Erro ao remover o membro.'), 'erro');
    }
  };

  const needsLinkAck = plan ? !plan.billing_exempt : true;
  const resultGroups = inviteResult ? {
    alreadyMember: inviteResult.results.filter(r => r.status === 'already_member').map(r => r.email),
    alreadyPending: inviteResult.results.filter(r => r.status === 'already_pending').map(r => r.email),
    failed: inviteResult.results.filter(r => r.status === 'error').map(r => r.email),
  } : null;

  const tabStyle = (tab: 'email' | 'link'): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-4)', background: 'transparent', border: 'none',
    borderBottom: `2px solid ${activeTab === tab ? 'var(--color-primary-400)' : 'transparent'}`,
    color: activeTab === tab ? 'var(--color-primary-400)' : 'var(--color-text-secondary)',
    fontWeight: activeTab === tab ? 'var(--font-medium)' : 'normal', cursor: 'pointer',
  });

  return (
    <AppLayout>
      <PageHeader title="Membros" description="Gerencie quem tem acesso à organização e suas credenciais." />

      {isAdmin && (
        <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', marginBottom: 'var(--space-8)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: '1px solid var(--color-border-default)', marginBottom: 'var(--space-5)', flexWrap: 'wrap' }}>
            <button type="button" style={tabStyle('email')} onClick={() => setActiveTab('email')}>
              <Mail size={16} /> Convite por e-mail
            </button>
            <button type="button" style={tabStyle('link')} onClick={() => setActiveTab('link')}>
              <Link2 size={16} /> Link de convite
            </button>
          </div>

          {activeTab === 'email' && (
            <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label style={labelStyle}>E-mails</label>
                <textarea
                  value={emailsText}
                  onChange={(e) => { setEmailsText(e.target.value); setInviteResult(null); setInviteError(null); }}
                  rows={4}
                  placeholder={'Cole os e-mails separados por vírgula ou linha. Ex:\nadvogado1@escritorio.com.br\nadvogado2@escritorio.com.br'}
                  style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
                {emailsText.trim() && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>{parsed.valid.length} e-mail(s) válido(s)</span>
                    {tooMany && <span style={{ color: 'var(--color-error)' }}>Máximo de {MAX_BULK_INVITES} e-mails por envio.</span>}
                    {parsed.invalid.length > 0 && (
                      <span style={{ color: 'var(--color-error)' }}>Formato inválido (serão ignorados): {parsed.invalid.join(', ')}</span>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <label style={labelStyle}>Papel</label>
                  <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={fieldStyle}>
                    <option value="member">Membro (apenas usa extensão)</option>
                    <option value="admin">Administrador (edita credenciais)</option>
                    {isOwner && <option value="owner">Proprietário</option>}
                  </select>
                </div>
                <button type="submit" disabled={isInviting || parsed.valid.length === 0 || tooMany} style={primaryButtonStyle(isInviting || parsed.valid.length === 0 || tooMany)}>
                  <UserPlus size={18} /> {isInviting ? 'Enviando...' : `Convidar${parsed.valid.length ? ` (${parsed.valid.length})` : ''}`}
                </button>
              </div>

              {inviteError && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{inviteError}</div>}

              {inviteResult && resultGroups && (
                <div style={{ fontSize: 'var(--text-sm)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <span style={{ color: 'var(--color-primary-400)' }}>{inviteResult.invited} convite(s) enviado(s).</span>
                  {resultGroups.alreadyMember.length > 0 && <span style={{ color: 'var(--color-text-secondary)' }}>Já eram membros: {resultGroups.alreadyMember.join(', ')}</span>}
                  {resultGroups.alreadyPending.length > 0 && <span style={{ color: 'var(--color-text-secondary)' }}>Já tinham convite pendente: {resultGroups.alreadyPending.join(', ')}</span>}
                  {resultGroups.failed.length > 0 && <span style={{ color: 'var(--color-error)' }}>Falharam (tente de novo): {resultGroups.failed.join(', ')}</span>}
                </div>
              )}
            </form>
          )}

          {activeTab === 'link' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
                Qualquer pessoa com o link e um e-mail do domínio escolhido pode pedir para entrar. Ela precisa confirmar pelo próprio e-mail antes de ser adicionada, e entra como <strong>Membro</strong>, sem acesso a nenhuma credencial até você liberar em Grupos.
              </p>

              {link && (
                <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary-400)', fontWeight: 'var(--font-medium)' }}>Link ativo</span>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <input readOnly value={link.url} onFocus={(e) => e.target.select()} style={{ ...fieldStyle, flex: '1 1 300px', fontFamily: 'monospace', fontSize: 'var(--text-xs)' }} />
                    <button type="button" onClick={handleCopyLink} style={primaryButtonStyle(false)}>
                      {copied ? <><Check size={16} /> Copiado</> : <><Copy size={16} /> Copiar</>}
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                      Restrito a <strong>@{link.domain}</strong> · expira em {new Date(link.expires_at).toLocaleString('pt-BR')}
                    </span>
                    <button type="button" onClick={handleRevokeLink} style={{ background: 'transparent', border: '1px solid var(--color-error)', color: 'var(--color-error)', padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}>
                      Desativar link
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleGenerateLink} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '2 1 240px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    <label style={labelStyle}>Domínio permitido</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>@</span>
                      <input type="text" value={linkDomain} onChange={(e) => setLinkDomain(e.target.value)} required placeholder="escritorio.com.br" style={{ ...fieldStyle, flex: 1 }} />
                    </div>
                  </div>
                  <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    <label style={labelStyle}>Validade</label>
                    <select value={linkHours} onChange={(e) => setLinkHours(Number(e.target.value))} style={fieldStyle}>
                      <option value={1}>1 hora</option>
                      <option value={12}>12 horas</option>
                      <option value={24}>24 horas</option>
                    </select>
                  </div>
                </div>

                {needsLinkAck && (
                  <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={linkAck} onChange={(e) => setLinkAck(e.target.checked)} style={{ marginTop: '3px' }} />
                    <span>
                      Estou ciente de que meu plano inclui {plan?.included_users ?? 10} usuários (hoje somos {plan?.current_members ?? '—'}) e que cada usuário que entrar por este link acima desse limite será cobrado como usuário adicional ({EXTRA_USER_PRICE}).
                    </span>
                  </label>
                )}

                {linkError && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{linkError}</div>}

                <div>
                  <button type="submit" disabled={isGenerating || !linkDomain.trim() || (needsLinkAck && !linkAck)} style={primaryButtonStyle(isGenerating || !linkDomain.trim() || (needsLinkAck && !linkAck))}>
                    <Link2 size={18} /> {isGenerating ? 'Gerando...' : link ? 'Gerar novo link' : 'Gerar link'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {planLimit && (
        <Modal pedirParaFechar={() => { if (!isInviting) setPlanLimit(null); }} rotuloId="limite-do-plano-titulo">
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', maxWidth: '480px', width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <AlertTriangle size={22} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <h3 id="limite-do-plano-titulo" style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', margin: 0 }}>Limite do plano excedido</h3>
            </div>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
              Esse número de usuários excede o limite do seu plano. Hoje a organização tem <strong>{planLimit.current_members}</strong> usuário(s) e o plano inclui <strong>{planLimit.included_users}</strong>.
              Com {planLimit.adding === 1 ? 'este convite' : `estes ${planLimit.adding} convites`}, passará a ter <strong>{planLimit.current_members + (planLimit.adding ?? 0)}</strong> — ou seja, <strong>{Math.max(0, planLimit.current_members + (planLimit.adding ?? 0) - planLimit.included_users)}</strong> acima do limite.
            </p>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              Cada usuário adicional custa <strong>{EXTRA_USER_PRICE}</strong>, cobrado automaticamente na assinatura.
              {isOwner && <> Veja os detalhes em <Link to="/billing" style={{ color: 'var(--color-primary-400)' }}>Plano e cobrança</Link>.</>}
            </p>
            <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
              <input type="checkbox" checked={planLimitAck} onChange={(e) => setPlanLimitAck(e.target.checked)} style={{ marginTop: '3px' }} />
              <span>Estou ciente de que isso excede o limite do meu plano e de que será cobrada a taxa adicional por usuário.</span>
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setPlanLimit(null)} disabled={isInviting} style={secondaryButtonStyle}>Cancelar</button>
              <button type="button" onClick={() => sendInvites(true)} disabled={!planLimitAck || isInviting} style={primaryButtonStyle(!planLimitAck || isInviting)}>
                {isInviting ? 'Enviando...' : 'Confirmar e convidar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', width: '100%' }}>
        {/* A1: busca e filtros acima da lista */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border-default)' }}>
          <div className="campo-busca">
            <input
              type="search"
              value={busca}
              onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
              placeholder="Buscar por nome ou e-mail"
              aria-label="Buscar membro por nome ou e-mail"
            />
            <Search size={16} />
          </div>
          <select className="filtro-select" value={filtroPapel} onChange={(e) => { setFiltroPapel(e.target.value as FiltroDePapel); setPagina(1); }} aria-label="Filtrar por papel">
            <option value="todos">Todos os papéis</option>
            <option value="owner">Proprietário</option>
            <option value="admin">Administrador</option>
            <option value="member">Membro</option>
          </select>
          <select className="filtro-select" value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value as FiltroDeStatus); setPagina(1); }} aria-label="Filtrar por status">
            <option value="todos">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="pendente">Pendente</option>
            <option value="inativo">Inativo</option>
          </select>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
            {temFiltro ? `${formatNumber(membrosFiltrados.length)} de ${formatNumber(members.length)}` : `${formatNumber(members.length)} ${members.length === 1 ? 'pessoa' : 'pessoas'}`}
          </span>
        </div>

        <table className="tabela-cartoes" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--space-3) var(--space-6)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Usuário</th>
              <th style={{ padding: 'var(--space-3) var(--space-6)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Status</th>
              <th style={{ padding: 'var(--space-3) var(--space-6)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}>Papel</th>
              {isAdmin && <th style={{ padding: 'var(--space-3) var(--space-6)', textAlign: 'right', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)' }}><span className="sr-only">Ações</span></th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? 4 : 3} style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-secondary)' }}>Carregando membros...</td>
              </tr>
            ) : membrosDaPagina.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 4 : 3} style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                  {temFiltro ? 'Ninguém encontrado com esses filtros.' : 'Nenhum membro ainda.'}
                </td>
              </tr>
            ) : membrosDaPagina.map((member) => {
              const isCurrentUser = member.id === user?.id;
              const canEditRole = isAdmin && !isCurrentUser && (membership?.role === 'owner' || member.role !== 'owner');
              const canRemove = isAdmin && !isCurrentUser && (membership?.role === 'owner' || member.role !== 'owner');
              // Mesma hierarquia do papel: owner renomeia qualquer um; admin renomeia members e admins
              const canEditName = isAdmin && (isOwner || member.role !== 'owner');
              const isEditingName = editingNameId === member.id;
              const status = statusDoMembro(member);

              // A1: as ações da linha ficam no menu ⋯, com Remover por último e em vermelho.
              const papeisPossiveis = (['member', 'admin', ...(isOwner ? ['owner'] : [])] as string[]).filter(p => p !== member.role);
              const acoes: ItemDoMenu[] = [
                ...(canEditName ? [{ rotulo: 'Editar nome', icone: <Pencil size={16} />, aoClicar: () => startEditingName(member.id, member.name) }] : []),
                ...(canEditRole ? papeisPossiveis.map(p => ({ rotulo: `Tornar ${nomeDoPapel(p).toLowerCase()}`, icone: <UserCog size={16} />, aoClicar: () => handleRoleChange(member, p) })) : []),
                ...(canRemove ? [{ rotulo: 'Remover da organização', icone: <UserMinus size={16} />, aoClicar: () => handleRemove(member), perigo: true }] : []),
              ];

              return (
                <tr key={member.id} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: 'var(--space-3) var(--space-6)' }}>
                    {isEditingName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <input
                          autoFocus
                          value={nameDraft}
                          maxLength={100}
                          onChange={(e) => setNameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveName();
                            if (e.key === 'Escape') setEditingNameId(null);
                          }}
                          placeholder="Vazio = nome do cadastro"
                          aria-label="Nome nesta organização"
                          style={{ ...fieldStyle, padding: 'var(--space-2)', minWidth: 0, flex: '1 1 180px' }}
                        />
                        <button type="button" className="botao-icone botao-icone--sem-borda" onClick={saveName} disabled={isSavingName} title="Salvar" aria-label="Salvar nome" style={{ color: 'var(--color-primary-400)' }}>
                          <Check size={16} />
                        </button>
                        <button type="button" className="botao-icone botao-icone--sem-borda" onClick={() => setEditingNameId(null)} disabled={isSavingName} title="Cancelar" aria-label="Cancelar edição do nome">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        {formatarNome(member.name) || 'Convite pendente'}
                        {isCurrentUser && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 'normal' }}>(Você)</span>}
                      </div>
                    )}
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>{member.email}</div>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-6)' }}>
                    <span className={`selo ${status === 'ativo' ? 'selo--sucesso' : status === 'pendente' ? 'selo--alerta' : 'selo--neutro'}`}>
                      {status === 'pendente' ? 'Pendente' : status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-6)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
                      {member.role === 'owner' && <ShieldAlert size={14} style={{ color: 'var(--color-primary-400)' }} />}
                      {nomeDoPapel(member.role)}
                    </span>
                  </td>
                  {isAdmin && (
                    <td style={{ padding: 'var(--space-3) var(--space-6)', textAlign: 'right' }}>
                      {acoes.length > 0 && <RowMenu itens={acoes} rotulo={`Ações de ${nomeNaLista(member)}`} />}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginacao pagina={paginaAtual} porPagina={MEMBROS_POR_PAGINA} total={membrosFiltrados.length} aoMudar={setPagina} />
      </div>
    </AppLayout>
  );
}
